import { ics } from 'calendar-link';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { MouseEventHandler, useContext, useEffect, useState } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';

// MUI
import CloseIcon from '@mui/icons-material/Close';

// GQL
import {
    ConfirmationPageEventQuery,
    DownloadAdditionalTermsDocument,
    DownloadTicketsDocument,
    EventObject,
} from '../../graphql/graphql';

// Formatter
import { useApolloClient } from '@apollo/client';
import { APP_URL, COMMUNITY_URL, EVENTS_URL } from '../../config';

import { formatUrl } from '@/utils/formatUrl.ts';
import EventIcon from '@mui/icons-material/Event';
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import { Box, Button, Container, Stack, Typography } from '@mui/joy';
import Link from '@mui/joy/Link';
import { IconButton, Snackbar } from '@mui/material';
import * as Sentry from '@sentry/react';
import { ThemeProvider } from '../../ThemeProvider';
import { downloadFile } from '../../utils/downloadFile.ts';
import HeaderJoyComponent from '../common/HeaderJoy.component';
import {
    StyledActionButton,
    StyledBorderButton,
    StyledButton,
    StyledButtonLight,
} from '../common/styled/ActionablesJoy.styled';
import OrderInfoJoy from '../orderSummary/orderEventInfoBlockJoy.component';
import { CartContext } from '../providers/cart.tsx';
import { lighten } from 'polished';

dayjs.extend(utc);
dayjs.extend(timezone);

export interface ConfirmationPageLoadedProps {
    event: ConfirmationPageEventQuery['event'];
    organizerPrimaryColor: string;
    hasAdditionalTerms: boolean;
    initialized: boolean;
    trackEvent: (eventName: string, params?: Record<string, any> | undefined) => void;
}

export interface CalEventObj {
    title: string;
    description: string;
    start: string;
    end: string;
    location: string;
}

export default function ConfirmationPageLoaded({
    event,
    organizerPrimaryColor,
    hasAdditionalTerms,
    initialized,
    trackEvent,
}: ConfirmationPageLoadedProps) {
    const { processedOrderId, resetCart, setProcessedOrderId, getCalculations, currentEventId } =
        useContext(CartContext);
    const [orderIdSnapshot] = useState(processedOrderId);
    const [{ lineItems, total }] = useState(getCalculations(event));

    const apollo = useApolloClient();

    useEffect(() => {
        if (initialized && processedOrderId) {
            // This is the most important conversion event
            trackEvent('Purchase', {
                content_type: 'event_ticket',
                content_ids: [currentEventId],
                contents: lineItems?.map((item) => ({
                    id: item.id,
                    quantity: item.quantity,
                    price: item.unitPrice,
                })),
                num_items: lineItems?.length || 1,
                value: total,
                currency: event?.currency || 'CAD',
                order_id: processedOrderId,
            });
        }
    }, [initialized, currentEventId, processedOrderId, trackEvent]);

    useEffect(() => {
        // We want to set the cart for the final time in component state/memory, then purge local storage
        if (orderIdSnapshot && lineItems?.length > 0) {
            resetCart(currentEventId as string);
            setProcessedOrderId(null);
        }
    }, []);

    const calEvent: CalEventObj = {
        title: event!.title,
        description: event!.description ?? '',
        start: dayjs.tz(event!.startDate, event!.timezone!).toString(),
        end: dayjs.tz(event!.endDate, event!.timezone!).toString(),
        location: event!.place === 'online' ? 'Online' : event!.address?.formattedAddress ?? '',
    };

    const [open, setOpen] = useState(false);

    const handleClick = () => {
        setOpen(true);
    };

    const handleClose = (_event: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
            return;
        }
        setOpen(false);
    };

    const handleDownloadTickets: MouseEventHandler<HTMLButtonElement> = async () => {
        if (!orderIdSnapshot) {
            return;
        }

        try {
            const resp = await apollo.mutate({
                mutation: DownloadTicketsDocument,
                variables: {
                    orderId: orderIdSnapshot,
                },
            });

            if (!resp.data?.downloadTicketsAtConfirmation) {
                return;
            }

            downloadFile(resp.data.downloadTicketsAtConfirmation);
        } catch (err) {
            console.error(err);
            Sentry.captureException(err);
        }
    };

    const handleDownloadAdditionalTerms = async () => {
        if (!orderIdSnapshot) {
            return;
        }
        try {
            const resp = await apollo.mutate({
                mutation: DownloadAdditionalTermsDocument,
                variables: {
                    shortOrderId: orderIdSnapshot,
                },
            });

            if (!resp.data?.downloadAdditionalTermsAtConfirmation) {
                return;
            }

            downloadFile(resp.data.downloadAdditionalTermsAtConfirmation, 'terms-and-conditions');
        } catch (err) {
            console.error(err);
            Sentry.captureException(err);
        }
    };

    const action = (
        <IconButton size="small" aria-label="close" color="inherit" onClick={handleClose}>
            <CloseIcon fontSize="small" />
        </IconButton>
    );

    const hasQuestionnaire =
        (event?.questionnaire && event?.questionnaire?.questions.length > 0) || false;

    const step =
        hasAdditionalTerms && hasQuestionnaire ? 5 : hasAdditionalTerms || hasQuestionnaire ? 4 : 3;

    return (
        <ThemeProvider>
            <>
                <Container sx={{ py: '60px' }}>
                    <HeaderJoyComponent
                        hasAdditionalTerms={hasAdditionalTerms}
                        previousPage={`/${event!.shortEventId}`}
                        step={step}
                        organizerPrimaryColor={organizerPrimaryColor}
                        headerTitle="Your order is confirmed!"
                        hasQuestionnaire={hasQuestionnaire}
                    />
                    <Typography sx={{ mb: '30px', fontSize: 18 }}>
                        Click on the button below to download your tickets, a confirmation email
                        will also be sent. If you do not see your confirmation email in your inbox
                        in a few minutes, please check your "junk mail" or "spam" folder.
                    </Typography>

                    {/* Check if we need it.......................................... */}

                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: {
                                md: 'row',
                                sm: 'column-reverse',
                                xs: 'column-reverse',
                            },
                            gap: 3,
                            justifyContent: 'space-between',
                        }}
                    >
                        <Box sx={{ marginBottom: 2, width: '100%', maxWidth: 466 }}>
                            <Box
                                sx={{
                                    backgroundColor: (theme) => theme.palette.neutral[50],
                                    padding: 3,
                                    borderRadius: 8,
                                }}
                            >
                                <Stack>
                                    <Box>
                                        <Typography sx={{ mb: 2, fontWeight: 600 }}>
                                            Download your ticket
                                        </Typography>
                                        <Box>
                                            <Typography level={'title-sm'} sx={{ mb: 0.5 }}>
                                                Order #{orderIdSnapshot}
                                            </Typography>
                                        </Box>
                                        <Stack sx={{ mb: 2 }}>
                                            {lineItems.map((item) => {
                                                return (
                                                    <Typography level={'body-sm'} key={item.id}>
                                                        {item.quantity} x {item.name}
                                                    </Typography>
                                                );
                                            })}
                                        </Stack>
                                    </Box>
                                    <StyledButton
                                        id={'tickets_download'}
                                        sx={{ minWidth: 164, mb: 2, alignSelf: 'flex-start' }}
                                        onClick={handleDownloadTickets}
                                        backgroundcolor={organizerPrimaryColor}
                                        startDecorator={<SaveAltIcon />}
                                    >
                                        Download Tickets
                                    </StyledButton>
                                    {hasAdditionalTerms && (
                                        <Button
                                            variant="outlined"
                                            startDecorator={<SaveAltIcon />}
                                            onClick={handleDownloadAdditionalTerms}
                                            sx={{
                                                color: '#32383E',
                                                border: '1px solid #CDD7E1',
                                                alignSelf: 'flex-start',
                                                '&:hover': {
                                                    backgroundColor: lighten(
                                                        0.5,
                                                        organizerPrimaryColor
                                                    ),
                                                },
                                            }}
                                        >
                                            Download Signed Terms
                                        </Button>
                                    )}
                                </Stack>
                            </Box>
                            {event?.postCheckoutCta?.link && (
                                <Box
                                    sx={{
                                        mt: 2,
                                        backgroundColor: (theme) => theme.palette.neutral[50],
                                        padding: 3,
                                        borderRadius: 8,
                                    }}
                                >
                                    <Typography sx={{ mb: 2, fontWeight: 600 }}>
                                        What’s next for you?
                                    </Typography>

                                    <Link
                                        href={event.postCheckoutCta.link}
                                        target={'_blank'}
                                        sx={{ textDecoration: 'none!important' }}
                                    >
                                        <StyledBorderButton
                                            id="external_link_button"
                                            backgroundcolor={organizerPrimaryColor}
                                            sx={{ minWidth: 164 }}
                                        >
                                            {event.postCheckoutCta.text}
                                        </StyledBorderButton>
                                    </Link>
                                </Box>
                            )}

                            <Box
                                sx={{
                                    mt: 2,
                                    backgroundColor: (theme) => theme.palette.neutral[50],
                                    padding: 3,
                                    borderRadius: 8,
                                }}
                            >
                                <Typography sx={{ mb: 2, fontWeight: 600 }}>
                                    Keep the fun going
                                </Typography>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: { xs: 'column', sm: 'row' },
                                    }}
                                >
                                    <Link
                                        href={EVENTS_URL}
                                        sx={{ textDecoration: 'none!important' }}
                                    >
                                        <StyledButtonLight
                                            id={'browse_more_button'}
                                            backgroundcolor={organizerPrimaryColor}
                                            sx={{
                                                marginTop: 0,
                                                mr: { xs: 0, sm: '15px' },
                                                mb: { xs: '15px', sm: 0 },
                                            }}
                                        >
                                            Browse More Events
                                        </StyledButtonLight>
                                    </Link>
                                    <Link
                                        href={`${APP_URL}/secure/my-attending`}
                                        sx={{ textDecoration: 'none!important' }}
                                    >
                                        <StyledButtonLight
                                            id={'view_upcoming_button'}
                                            backgroundcolor={organizerPrimaryColor}
                                        >
                                            View Upcoming Events
                                        </StyledButtonLight>
                                    </Link>
                                </Box>
                            </Box>
                        </Box>
                        {/* ............................................................................................. */}
                        <Box>
                            <Box
                                sx={{
                                    backgroundColor: (theme) => theme.palette.neutral[50],
                                    padding: 3,
                                    borderRadius: 8,
                                }}
                            >
                                <Box>
                                    <Typography
                                        sx={{ fontSize: 24, fontWeight: 600, marginBottom: 2 }}
                                    >
                                        Your Order Information
                                    </Typography>
                                </Box>
                                <OrderInfoJoy
                                    event={event}
                                    organizerPrimaryColor={organizerPrimaryColor}
                                />
                                <Box>
                                    <Box>
                                        <Typography level={'title-sm'} sx={{ mb: 1, mt: 1 }}>
                                            Order #{orderIdSnapshot}
                                        </Typography>
                                    </Box>
                                    <Stack sx={{ mb: '30px' }}>
                                        {lineItems.map((item) => {
                                            return (
                                                <Typography level={'body-sm'} key={item.id}>
                                                    {item.quantity} x {item.name}
                                                </Typography>
                                            );
                                        })}
                                    </Stack>
                                </Box>
                                <Box>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            mb: { xs: '15px', md: 0 },
                                            alignItems: 'flex-start',
                                            justifyContent: 'start',
                                            flexWrap: 'wrap',
                                            flexDirection: { xs: 'column', sm: 'row' },
                                        }}
                                    >
                                        <CopyToClipboard
                                            text={`${COMMUNITY_URL}/${formatUrl(
                                                event as EventObject
                                            )}`}
                                            onCopy={handleClick}
                                        >
                                            <StyledActionButton
                                                id="copy_url_button"
                                                startDecorator={<InsertLinkIcon />}
                                                setcolor={organizerPrimaryColor}
                                                variant="plain"
                                                sx={{
                                                    mr: { xs: 0, sm: '15px' },
                                                    alignSelf: 'flex-start',
                                                }}
                                            >
                                                Copy Event URL
                                            </StyledActionButton>
                                        </CopyToClipboard>
                                        <Snackbar
                                            open={open}
                                            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                                            autoHideDuration={6000}
                                            onClose={handleClose}
                                            message="Copied to Clipboard!"
                                            action={action}
                                            sx={{
                                                backgroundColor: organizerPrimaryColor,
                                                borderRadius: '4px',
                                                '.MuiPaper-elevation6': {
                                                    backgroundColor: organizerPrimaryColor,
                                                },
                                            }}
                                        />
                                        <StyledActionButton
                                            id={'add_to_calendar'}
                                            startDecorator={<EventIcon />}
                                            setcolor={organizerPrimaryColor}
                                            variant="plain"
                                            onClick={() => {
                                                const a = document.createElement('a');
                                                a.href = ics(calEvent);
                                                a.download;
                                                a.click();
                                            }}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                alignSelf: 'flex-start',
                                            }}
                                        >
                                            Add To Calendar
                                        </StyledActionButton>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Container>
            </>
        </ThemeProvider>
    );
}
